<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import ProductCard from './components/ProductCard.vue'
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import Newsletter from './components/Newsletter.vue'


const isUnderwater = ref(false)
const handleScroll = () => {
  const scrollPosition = window.scrollY
  const windowHeight = window.innerHeight
  isUnderwater.value = scrollPosition > windowHeight * 0.7
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const products = [
  {
    id: 1,
    name: 'Krill Oil Ultra',
    description: 'Premium Antarctic krill oil rich in omega-3s EPA & DHA in phospholipid form for superior absorption.',
    features: ['High Bioavailability', 'Sustainable Source', 'Pure Antarctic Krill'],
    price: 49.99,
    image: 'https://placehold.co/300x200/247BA0/ffffff?text=Krill+Oil+Ultra'
  },
  {
    id: 2,
    name: 'Krill Oil QH',
    description: 'Advanced formula combining krill oil with Coenzyme Q10 for enhanced heart and cellular health.',
    features: ['With Coenzyme Q10', 'Heart Health', 'Cellular Energy'],
    price: 59.99,
    image: 'https://placehold.co/300x200/0A2463/ffffff?text=Krill+Oil+QH'
  },
]
</script>

<template>
  <main>
    <Header />
    <div class="arctic-bg">
      <div class="underwater" :class="{ visible: isUnderwater }">
        <!-- Krill swarm -->
        <div v-for="n in 8" :key="`krill1-${n}`" class="krill"
             :style="{
               left: `${Math.random() * 80 + 10}%`,
               top: `${Math.random() * 60 + 20}%`,
               '--swim-x': `${Math.random() * 60 - 30}px`,
              animationDelay: `${Math.random() * -15}s`,
              '--direction': Math.random() > 0.5 ? '1' : '-1'
             }">
          <div class="krill-body">
            <div class="krill-eye"></div>
            <div class="krill-segment"></div>
            <div class="krill-segment"></div>
            <div class="krill-segment"></div>
            <div class="krill-legs">
              <div class="krill-leg"></div>
              <div class="krill-leg"></div>
              <div class="krill-leg"></div>
              <div class="krill-leg"></div>
              <div class="krill-leg"></div>
              <div class="krill-leg"></div>
            </div>
            <div class="krill-antenna"></div>
            <div class="krill-antenna"></div>
            <div class="krill-tail"></div>
          </div>
        </div>
        
        <!-- Bubbles -->
        <div v-for="n in 20" :key="`bubble-${n}`" 
             class="bubble"
             :style="{
               left: `${Math.random() * 100}%`,
               width: `${Math.random() * 10 + 5}px`,
               height: `${Math.random() * 10 + 5}px`,
               animationDelay: `${Math.random() * -8}s`
             }">
        </div>
      </div>
      <div class="aurora"></div>
      <div class="ice-mountains"></div>

      <!-- Enhanced snow particles -->
      <div v-for="n in 100" :key="n" 
           class="snow"
           :style="{
             left: `${Math.random() * 100}%`,
             top: `${Math.random() * 100}%`,
             animationDelay: `${Math.random() * 20}s`,
             transform: `scale(${Math.random() * 0.5 + 0.5})`
           }">
      </div>
      
      <!-- Ice crystals -->
      <div v-for="n in 50" :key="`crystal-${n}`"
           class="ice-crystal"
           :style="{
             left: `${Math.random() * 100}%`,
             top: `${Math.random() * 100}%`,
             animationDelay: `${Math.random() * 4}s`,
             transform: `scale(${Math.random() * 0.8 + 0.5})`
           }">
      </div>
      </div>
    <section class="hero">
      <div class="container">
        <h1 class="text-6xl font-bold mb-6" style="font-size: 4rem; margin-bottom: 1.5rem;">
          Future of Omega-3
          <span style="color: var(--accent)">Supplementation</span>
        </h1>
        <p class="text-xl mb-8" style="font-size: 1.25rem; margin-bottom: 2rem; max-width: 600px;">
          Discover the power of Antarctic krill oil, delivering essential omega-3s in their most bioavailable form for optimal health and vitality.
        </p>
        <button class="btn">Explore Products</button>
      </div>
    </section>

    <section id="products" class="section">
      <div class="container">
        <h2 style="font-size: 2.5rem; margin-bottom: 1rem;">Our Products</h2>
        <p style="color: var(--secondary); margin-bottom: 3rem;">Advanced formulations for modern wellness</p>
        
        <div class="grid">
          <ProductCard 
            v-for="product in products" 
            :key="product.id"
            :product="product"
          />
        </div>
      </div>
    </section>

    <section id="science" class="section" style="background: rgba(36, 123, 160, 0.1);">
      <div class="container">
        <h2 style="font-size: 2.5rem; margin-bottom: 2rem; text-align: center;">Why Choose Krilom?</h2>
        <div class="grid">
          <div class="product-card">
            <h3 style="font-size: 1.5rem; margin-bottom: 1rem;">Superior Absorption</h3>
            <p>Our phospholipid-bound omega-3s are better absorbed than fish oil triglycerides.</p>
          </div>
          <div class="product-card">
            <h3 style="font-size: 1.5rem; margin-bottom: 1rem;">Sustainable Source</h3>
            <p>Responsibly harvested from Antarctic waters with minimal environmental impact.</p>
          </div>
          <div class="product-card">
            <h3 style="font-size: 1.5rem; margin-bottom: 1rem;">Advanced Formulas</h3>
            <p>Innovative combinations with other beneficial compounds like Coenzyme Q10.</p>
          </div>
        </div>
      </div>
    </section>
    
    <section id="contact">
      <Newsletter />
    </section>
    
    <section id="research" class="section research-section">
      <div class="container">
        <h2 style="font-size: 2.5rem; margin-bottom: 2rem; text-align: center;">Research & Publications</h2>
        <div class="research-grid">
          <div class="research-card">
            <div class="research-icon">📊</div>
            <h3>Clinical Studies</h3>
            <p>Recent studies show 47% higher absorption rates of omega-3s from krill oil compared to fish oil sources.</p>
            <a href="#" class="research-link">Read More →</a>
          </div>
          
          <div class="research-card">
            <div class="research-icon">🔬</div>
            <h3>Lab Analysis</h3>
            <p>Our proprietary extraction process preserves the natural phospholipid structure of omega-3s.</p>
            <a href="#" class="research-link">View Report →</a>
          </div>
          
          <div class="research-card">
            <div class="research-icon">📈</div>
            <h3>Bioavailability Data</h3>
            <p>Peer-reviewed research confirms superior integration of krill-based omega-3s into cell membranes.</p>
            <a href="#" class="research-link">Learn More →</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </main>
</template>